import { Box, Typography, useTheme, Button, TextField } from "@mui/material";
import { useSelector }  from "react-redux";
import { useState }     from "react";
import moment           from "moment";

import FeedbackIcon      from "@mui/icons-material/Feedback";
import AddIcon           from "@mui/icons-material/Add";
import FilterListIcon    from "@mui/icons-material/FilterList";
import VisibilityIcon    from "@mui/icons-material/Visibility";
import EditOutlinedIcon  from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StarIcon          from "@mui/icons-material/Star";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import FixedSizeDialog   from "components/fixedSizeDialog";
import CustomerFeedbackForm     from "../forms/customerFeedbackForm";
import EditCustomerFeedbackForm from "../forms/editEustomerFeedbackForm";
import ViewCustomerFeedbackForm from "../forms/viewCustomerFeedbackForm";
import { useCustomerFeedbackList } from "api/hooks/customer";
import { requester } from "api/pesabackend";

const RATING_LABELS = {
  0.5: "Very Disappointing", 1: "Disappointing",
  1.5: "Very Poor",          2: "Poor",
  2.5: "Ok",                 3: "Ok+",
  3.5: "Good",               4: "Good+",
  4.5: "Excellent",          5: "Excellent+",
};

const PERIODS = ["TODAY", "THIS WEEK", "THIS MONTH", "CUSTOM"];

/* ── Rating badge ── */
const RatingBadge = ({ rating }) => {
  const label = RATING_LABELS[rating] ?? "—";
  const isGood = rating >= 3.5;
  const isOk   = rating >= 2.5 && rating < 3.5;
  const color   = isGood ? "#1D9E75" : isOk ? "#BA7517" : "#e24b4a";
  const bg      = isGood ? "rgba(29,158,117,0.1)" : isOk ? "rgba(186,117,23,0.1)" : "rgba(226,75,74,0.1)";

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "3px", borderRadius: "20px", backgroundColor: bg }}>
      <StarIcon sx={{ fontSize: "11px", color }} />
      <Typography sx={{ fontSize: "11px", fontWeight: 600, color, lineHeight: 1 }}>{label}</Typography>
    </Box>
  );
};

/* ── Follow-up status pill ── */
const FollowUpPill = ({ status }) => {
  const isPending = status === 0;
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", px: "8px", py: "3px", borderRadius: "20px", backgroundColor: isPending ? "rgba(186,117,23,0.1)" : "rgba(29,158,117,0.12)" }}>
      <Box sx={{ width: "5px", height: "5px", borderRadius: "50%", backgroundColor: isPending ? "#BA7517" : "#1D9E75", flexShrink: 0 }} />
      <Typography sx={{ fontSize: "11px", fontWeight: 600, color: isPending ? "#BA7517" : "#1D9E75", lineHeight: 1 }}>
        {isPending ? "Pending" : "Completed"}
      </Typography>
    </Box>
  );
};

export default function CustomerFeedbackList({ setIsSuccess }) {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const { customerFeedbackList, isLoadingCustomerFeedbackList, setNewCustomerFeedbackData, setFilter } =
    useCustomerFeedbackList(1, business_branch_id);

  const [open,        setOpen]        = useState(false);
  const [openEdit,    setOpenEdit]    = useState(false);
  const [openDelete,  setOpenDelete]  = useState(false);
  const [openView,    setOpenView]    = useState(false);
  const [viewData,    setViewData]    = useState({});
  const [clickedItem, setClickedItem] = useState("");
  const [showCustom,  setShowCustom]  = useState(false);
  const [activePeriod, setActivePeriod] = useState("THIS WEEK");
  const [startDate,   setStartDate]   = useState(moment().startOf("week").format("YYYY-MM-DD"));
  const [endDate,     setEndDate]     = useState(moment().endOf("week").format("YYYY-MM-DD"));

  const inputSx = {
    "& .MuiInputBase-root": { fontSize: "13px", height: "36px", backgroundColor: cardBg, borderRadius: "8px" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
    "& .MuiInputLabel-root": { fontSize: "12px" },
  };

  const handlePeriod = (val) => {
    setActivePeriod(val);
    if (val === "TODAY")      { setStartDate(moment().format("YYYY-MM-DD")); setEndDate(moment().format("YYYY-MM-DD")); setShowCustom(false); }
    if (val === "THIS WEEK")  { setStartDate(moment().startOf("week").format("YYYY-MM-DD")); setEndDate(moment().endOf("week").format("YYYY-MM-DD")); setShowCustom(false); }
    if (val === "THIS MONTH") { setStartDate(moment().startOf("month").format("YYYY-MM-DD")); setEndDate(moment().endOf("month").format("YYYY-MM-DD")); setShowCustom(false); }
    if (val === "CUSTOM")     { setShowCustom(true); }
  };

  const handleClickEditOpen = async (id) => {
    const res = await requester.get(`/customers/feedback/get/${id}`);
    if (res.status === 200) { setViewData(res.data); setOpenEdit(true); }
  };

  const handleClickDeleteOpen = async (id) => {
    setClickedItem(id);
    await requester.get(`/customers/feedback/get/${id}`);
    setOpenDelete(true);
  };

  const handleClickDelete = async () => {
    const res = await requester.post(`/customers/feedback/delete/${clickedItem}`);
    if (res.status === 201) { setIsSuccess(true); setNewCustomerFeedbackData(true); setOpenDelete(false); }
  };

  const handleClickView = async (id) => {
    const res = await requester.get(`/customers/feedback/get_form/${id}`);
    if (res.status === 200) { setViewData(res.data.data); setOpenView(true); }
  };

  const columns = [
    { label: "#",               width: 40  },
    { label: "Date Added"                  },
    { label: "Customer"                    },
    { label: "Contact"                     },
    { label: "Rating"                      },
    { label: "Follow-Up"                   },
    { label: "Appointment"                 },
    { label: "Followed Up By"              },
    { label: "Actions",         align: "center" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{
            width: "40px", height: "40px", borderRadius: "10px",
            backgroundColor: "rgba(29,158,117,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: accent, flexShrink: 0,
          }}>
            <FeedbackIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
              Customer Feedback
            </Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>
              {customerFeedbackList.length} total records
            </Typography>
          </Box>
        </Box>

        <Button
          onClick={() => setOpen(true)}
          startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
          sx={{
            fontSize: "13px", fontWeight: 500,
            px: "1rem", py: "0.45rem", borderRadius: "8px",
            backgroundColor: accent, color: "#fff", textTransform: "none",
            "&:hover": { backgroundColor: "#0F6E56" },
          }}
        >
          Add Feedback
        </Button>
      </Box>

      {/* ── Filter bar ── */}
      <Box sx={{
        display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.75rem",
        p: "0.75rem 1rem",
        backgroundColor: cardBg,
        border: `1px solid ${border}`,
        borderRadius: "10px",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", color: accent }}>
          <FilterListIcon sx={{ fontSize: "16px" }} />
          <Typography sx={{ fontSize: "11px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em" }}>
            Period
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
          {PERIODS.map((p) => (
            <Box key={p} onClick={() => handlePeriod(p)} sx={{
              px: "0.65rem", py: "0.25rem", borderRadius: "20px",
              fontSize: "12px", fontWeight: 500, cursor: "pointer",
              border: `1px solid ${activePeriod === p ? accent : border}`,
              color: activePeriod === p ? accent : textSec,
              backgroundColor: activePeriod === p ? "rgba(29,158,117,0.1)" : "transparent",
              transition: "all 0.15s",
              "&:hover": { borderColor: accent, color: accent },
            }}>
              {p === "TODAY" ? "Today" : p === "THIS WEEK" ? "This week" : p === "THIS MONTH" ? "This month" : "Custom"}
            </Box>
          ))}
        </Box>

        {showCustom && (
          <>
            <TextField label="Start date" type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} sx={{ ...inputSx, width: "150px" }} />
            <TextField label="End date" type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} sx={{ ...inputSx, width: "150px" }} />
          </>
        )}

        <Button
          onClick={() => setFilter({ start: startDate, end: endDate })}
          sx={{
            fontSize: "12px", fontWeight: 500, px: "1rem", py: "0.35rem",
            borderRadius: "8px", backgroundColor: accent, color: "#fff",
            textTransform: "none", "&:hover": { backgroundColor: "#0F6E56" },
          }}
        >
          Apply
        </Button>
      </Box>

      {/* ── Table ── */}
      <ReportTable
        columns={columns}
        rows={customerFeedbackList}
        loading={isLoadingCustomerFeedbackList}
        emptyMessage="No feedback records found for this period"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell>{index + 1}</RCell>
            <RCell>{moment(row.date_added).format("LL")}</RCell>
            <RCell>
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>
                {row.related_customer
                  ? `${row.related_customer.related_user.first_name} ${row.related_customer.related_user.last_name}`
                  : <Typography sx={{ fontSize: "12px", color: textSec, fontStyle: "italic" }}>N/A</Typography>}
              </Typography>
            </RCell>
            <RCell>{row.phone_number || "—"}</RCell>
            <RCell><RatingBadge rating={row.rating} /></RCell>
            <RCell><FollowUpPill status={row.status} /></RCell>
            <RCell>{moment(row.follow_up_appointed_date).format("LL")}</RCell>
            <RCell>
              {row.followed_up_by
                ? `${row.followed_up_by.first_name} ${row.followed_up_by.last_name}`
                : <Typography sx={{ fontSize: "12px", color: textSec }}>—</Typography>}
            </RCell>
            <RCell align="center">
              <Box sx={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                <Box onClick={() => handleClickView(row.id)} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(29,158,117,0.3)", cursor: "pointer", color: accent, fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" } }}>
                  <VisibilityIcon sx={{ fontSize: "14px" }} /> View
                </Box>
                {row.status === 0 && (
                  <>
                    <Box onClick={() => handleClickEditOpen(row.id)} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(186,117,23,0.3)", cursor: "pointer", color: "#BA7517", fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(186,117,23,0.08)" } }}>
                      <EditOutlinedIcon sx={{ fontSize: "14px" }} /> Edit
                    </Box>
                    <Box onClick={() => handleClickDeleteOpen(row.id)} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(226,75,74,0.3)", cursor: "pointer", color: "#e24b4a", fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(226,75,74,0.08)" } }}>
                      <DeleteOutlineIcon sx={{ fontSize: "14px" }} /> Delete
                    </Box>
                  </>
                )}
              </Box>
            </RCell>
          </RRow>
        )}
      />

      {/* ── Dialogs — all outside the row render loop ── */}
      <FixedSizeDialog
        open={open}
        handleClose={() => setOpen(false)}
        dialogHeading="Add Customer Feedback"
        negativeResponse="Cancel"
        maxWidth="md"
        content={<Box p="1rem"><CustomerFeedbackForm setOpen={setOpen} setNewCustomerFeedbackData={setNewCustomerFeedbackData} setIsSuccess={setIsSuccess} /></Box>}
      />

      <FixedSizeDialog
        open={openView}
        handleClose={() => setOpenView(false)}
        dialogHeading="View Customer Feedback"
        negativeResponse="Close"
        maxWidth="md"
        scroll="body"
        content={<Box p="1rem"><ViewCustomerFeedbackForm viewData={viewData} setNewCustomerFeedbackData={setNewCustomerFeedbackData} setIsSuccess={setIsSuccess} setOpen={setOpenView} /></Box>}
      />

      <FixedSizeDialog
        open={openEdit}
        handleClose={() => setOpenEdit(false)}
        dialogHeading="Edit Customer Feedback"
        negativeResponse="Cancel"
        maxWidth="md"
        scroll="body"
        content={<Box p="1rem"><EditCustomerFeedbackForm editData={viewData.data} setNewCustomerFeedbackData={setNewCustomerFeedbackData} setIsSuccess={setIsSuccess} setOpen={setOpenEdit} /></Box>}
      />

      <FixedSizeDialog
        open={openDelete}
        handleClose={() => setOpenDelete(false)}
        dialogHeading="Delete this feedback?"
        negativeResponse="Cancel"
        positiveResponse="Yes, delete"
        handleSubmit={handleClickDelete}
        maxWidth="sm"
        content={
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px", backgroundColor: "rgba(226,75,74,0.07)", border: "1px solid rgba(226,75,74,0.2)", borderRadius: "8px", p: "0.875rem 1rem" }}>
            <DeleteOutlineIcon sx={{ fontSize: "18px", color: "#e24b4a", flexShrink: 0 }} />
            <Typography sx={{ fontSize: "13px", color: isDark ? "#f09595" : "#a32d2d", lineHeight: 1.6 }}>
              This will permanently delete the feedback record. This action cannot be undone.
            </Typography>
          </Box>
        }
      />
    </Box>
  );
}